import { useEffect, useState } from "react"
import { Button } from "@components/ui"
import { Message } from "@utils/handleMessage"
import MessageBlock from "../MessageBlock"
import { mutate } from "swr"
import getEthFromWei from "@utils/getEthFromWei"
import getUnreleasedData from "@utils/getUnreleasedData"
import TransferFormInputBlock from "../TransferFormInputBlock"
import TransferFormNotes from "../TransferFormNotes"
import MySwitch from "../MySwitch"
import { useAddRecentTransaction } from "@rainbow-me/rainbowkit"
import { useEthersSigner } from "@utils/ethers"
import { UnreleasedAmount } from "pages/api/account/[address]/unreleased"
import executeTransaction from "@utils/executeTransaction"
import SliceTransferConfig from "@lib/handlers/chain/usePrepareSliceTransfer/usePrepareSliceTransfer"
import { useConfig, useWriteContract } from "wagmi"

type Props = {
  account: string
  slicerId: string
  slicerAddress: string
  ownedSlices: number
  totalSlices: number
  minimumSlices: number
}

const TransferForm = ({
  account,
  slicerId,
  slicerAddress,
  ownedSlices,
  totalSlices,
  minimumSlices
}: Props) => {
  const config = useConfig()
  // const signer = useEthersSigner({
  //   chainId: Number(process.env.NEXT_PUBLIC_CHAIN_ID)
  // })
  const addRecentTransaction = useAddRecentTransaction()

  const [unreleased, setUnreleased] = useState<UnreleasedAmount[]>([])
  const [batchMode, setBatchMode] = useState(false)
  const [addresses, setAddresses] = useState([""])
  const [shares, setShares] = useState([0])
  const [toRelease, setToRelease] = useState(true)
  const [totalShares, setTotalShares] = useState(0)

  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<Message>({
    message: "",
    messageStatus: "success"
  })
  // const [logs, setLogs] = useState<LogDescription[]>()
  // const eventLog = getLog(logs, "TransferSingle")

  const unreleasedEth = getEthFromWei(unreleased[0]?.eth, true)
  const cleanedAddresses = addresses.filter((el) => el != "")
  const cleanedShares = shares.filter((el) => el != 0)

  const { writeContractAsync } = useWriteContract()

  const submit = async (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault()
    try {
      await executeTransaction(
        config,
        async () =>
          await writeContractAsync(
            SliceTransferConfig(
              batchMode,
              account,
              cleanedAddresses,
              Number(slicerId),
              cleanedShares,
              toRelease
            )
          ),
        setLoading,
        `${batchMode ? "Batch " : ""}Transfer slices | Slicer #${slicerId}`,
        addRecentTransaction,
        () => setSuccess(true)
      )
    } catch (error) {
      console.log(error)
    }
  }

  const reset = () => {
    mutate(`/api/account/${account}/slicers`)
    setAddresses([])
    setShares([])
    setSuccess(false)
  }

  useEffect(() => {
    if (account && slicerAddress && !success) {
      getUnreleasedData(account, [slicerAddress], setUnreleased)
    }
    return () => setUnreleased([])
  }, [slicerAddress, account, success])

  return (
    <div className="relative px-4 py-12 bg-white shadow-xl sm:px-10 rounded-2xl">
      {!success ? (
        <form onSubmit={submit}>
          <div className="space-y-6">
            <TransferFormInputBlock
              batchMode={batchMode}
              addresses={addresses}
              shares={shares}
              totalShares={totalShares}
              ownedSlices={ownedSlices}
              totalSlices={totalSlices}
              minimumSlices={minimumSlices}
              setAddresses={setAddresses}
              setShares={setShares}
              setTotalShares={setTotalShares}
            />
            {/* {unreleasedEth && unreleasedEth != 0 ? ( */}
            <div className="flex items-center justify-end pt-4 pb-2 space-x-4">
              <p>Trigger release during transfer</p>
              <MySwitch enabled={toRelease} setEnabled={setToRelease} />
            </div>
            {/* ) : null} */}
            <TransferFormNotes
              unreleasedEth={Number(unreleasedEth)}
              ownedSlices={ownedSlices}
              slicesToTransfer={totalShares}
              minimumSlices={minimumSlices}
              toRelease={toRelease}
            />
            <div>
              <div className="pt-3">
                <Button
                  label={unreleased.length == 0 ? "Wait" : "Transfer"}
                  loading={loading}
                  type="submit"
                  disabled={unreleased.length == 0}
                />
              </div>
              <div className="mt-8">
                <a
                  className="highlight"
                  onClick={() => setBatchMode(!batchMode)}
                >
                  {batchMode ? "Single transfer" : "Batch transfer"}
                </a>
              </div>
              <div>
                <MessageBlock msg={message} />
              </div>
            </div>
          </div>
        </form>
      ) : (
        <>
          <p className="font-medium">Slices transferred successfully! </p>

          <div className="py-8">
            <Button label="Go to your slicers" href="/profile" />
          </div>
          <a className="highlight" onClick={() => reset()}>
            Make a new transfer
          </a>
        </>
      )}
    </div>
  )
}

export default TransferForm

// TODO: When multiple currencies are added, extend unreleased checks/notes to all accepted currencies & set toRelease to false if all are 0.
